import { FC, useEffect, useState, MouseEvent, useRef } from "react";
import { styled } from "@mui/material/styles";
import {
  Box,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { AdminLayout } from "../../../../components/layouts/admin-layout/admin-layout";
import { CustomButton } from "../../../../components";
import { Colors } from "../../../../configs";
import { useSnackbarContext } from "../../../../providers/SnackbarProvider";
import { AddEditBoxDrawer } from "../../../../components/admin/add-edit-box-drawer/AddEditBoxDrawer";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { getAvailableBoxAction } from "../../../../redux/actions/boxStorageAction";

const sampleBoxConfigs = [
  {
    id: 1,
    boxStorageId: "BOX-001",
    boxSize: "Small",
    timePeriod: "6 Months",
    slotCount: 10,
  },
  {
    id: 2,
    boxStorageId: "BOX-002",
    boxSize: "Medium",
    timePeriod: "1 Year",
    slotCount: 15,
  },
  {
    id: 3,
    boxStorageId: "BOX-003",
    boxSize: "Large",
    timePeriod: "6 Months",
    slotCount: 20,
  },
];

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    fontWeight: "bold",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

export const BoxConfig: FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBoxId, setSelectedBoxId] = useState<number | undefined>(
    undefined
  );
  const [openDrawer, setOpenDrawer] = useState(false);
  const snackBar = useSnackbarContext();

  const [getBoxConfigs, setGetBoxConfigs] = useState(sampleBoxConfigs);
  const [loading, setLoading] = useState(false);

  const selectedBox = getBoxConfigs.find((b) => b.id === selectedBoxId);
  const normalizeId = (s?: string) => (s ?? "").trim().toLowerCase();
  const dispatch = useAppDispatch();

  const { boxesConfig, createBox } = useAppSelector(
    (state) => state.boxStorage
  );

  useEffect(() => {
    dispatch(getAvailableBoxAction());
  }, [createBox]);

  useEffect(() => {
    dispatch(getAvailableBoxAction());
  }, []);

  const upsertBox = async (
    payload: {
      boxStorageId: string;
      boxSize: string;
      timePeriod: string;
      slotCount: number;
    },
    id?: number
  ): Promise<void> => {
    const normalized = normalizeId(payload.boxStorageId);

    const duplicate = getBoxConfigs.some(
      (b) => normalizeId(b.boxStorageId) === normalized && b.id !== id
    );

    if (duplicate) {
      return Promise.reject(new Error("Box Storage ID already exists"));
    }

    setGetBoxConfigs((prev) => {
      if (id) {
        return prev.map((b) => (b.id === id ? { ...b, ...payload } : b));
      }
      const nextId = prev.length ? Math.max(...prev.map((b) => b.id)) + 1 : 1;
      return [...prev, { id: nextId, ...payload }];
    });

    return Promise.resolve();
  };

  const componentRef = useRef<HTMLDivElement>(null);

  const handleClick = (event: MouseEvent<HTMLButtonElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedBoxId(id);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <AdminLayout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "3rem",
          marginTop: "4.357rem",
          paddingInline: "5rem",
          width: "100%",
        }}
      >
        <AddEditBoxDrawer
          open={openDrawer}
          toggleDrawer={() => setOpenDrawer(false)}
          populateFor={selectedBox}
          onSave={upsertBox}
          existingBoxes={getBoxConfigs}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" sx={{ color: Colors.TEXT_GRAY_DARK }}>
            Box Config
          </Typography>
          <CustomButton
            sx={{ width: "11rem", height: "3rem" }}
            disabled={loading}
            onClick={() => {
              setOpenDrawer(true);
              setSelectedBoxId(undefined);
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Add New"
            )}
          </CustomButton>
        </Box>

        {/* Table */}
        <Box
          sx={{
            width: "100%",
            height: "72vh",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "0.5rem",
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f1f1",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#888",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "#555",
            },
          }}
        >
          <Table sx={{ width: "100%" }} aria-label="box config table">
            <TableHead>
              <TableRow>
                <StyledTableCell>Box Id</StyledTableCell>
                <StyledTableCell>Box Size</StyledTableCell>
                <StyledTableCell>Box Quantity</StyledTableCell>
                <StyledTableCell>Remaining Slots</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <StyledTableRow>
                  <StyledTableCell colSpan={5} sx={{ textAlign: "center" }}>
                    <CircularProgress size={24} color="inherit" />
                  </StyledTableCell>
                </StyledTableRow>
              )}
              {!loading &&
                boxesConfig?.body.map((row) => (
                  <StyledTableRow key={row.id}>
                    <StyledTableCell>{row.id}</StyledTableCell>
                    <StyledTableCell>{row.alias}</StyledTableCell>
                    <StyledTableCell>{row.quantity}</StyledTableCell>
                    <StyledTableCell>{row.remainingSlots}</StyledTableCell>
                    <StyledTableCell>
                      <IconButton onClick={(e) => handleClick(e, row.id)}>
                        <MoreHorizIcon sx={{ fontSize: "2rem" }} />
                      </IconButton>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
            </TableBody>
          </Table>
        </Box>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          MenuListProps={{ "aria-labelledby": "box-menu-button" }}
        >
          <MenuItem
            onClick={() => {
              snackBar.showSnackbar("Edit is coming soon", "info");
            }}
          >
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit Box</ListItemText>
          </MenuItem>

          <MenuItem
            onClick={() => {
              snackBar.showSnackbar("Delete is coming soon", "info");
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete Box</ListItemText>
          </MenuItem>
        </Menu>
      </Box>
    </AdminLayout>
  );
};
